import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import {
  browserName,
  browserVersion,
  isMobile,
  mobileModel,
  mobileVendor,
  osName,
  osVersion
} from "react-device-detect";
import { ChildInfo } from "../../../../models/home-rows";
import {
  MobileLoginStep1Request,
  MobileLoginStep1Response,
  MobileLoginStep2Request,
  MobileLoginStep2Response,
  MobileLoginStep3Response
} from "../../../../models/login";
import { API } from "../../../../services/api-services";
import AppContext from "../../../../services/app-context-services";
import { trackEvent } from "../../../../services/ga-services";
import AppNotifCtx from "../../../../services/notif-context-services";
import { isNumeric } from "../../../../services/text-services";
import FetchData from "../../../shared/fetch-data/fetch-data";
import MobileNumberStep2 from "./mobile2-number-step.component";
import SelectChildStep from "./select-child.component";
import VerifyStep2 from "./verify2-step.component";
import { ROUTES } from "../../../../services/router-services";
import { RestOperator } from "../../../cold-start/models/model";

type Page = "mobile-number-page" | "verify-page" | "select-child";
type Fetch =
  | "no-fetch"
  | "fetch-1"
  | "fetch-2"
  | "fetch-3"
  | "fetch-4"
  | "fetch-5";

interface AppLoginProps {
  container: "page" | "modal";
  onLoginComplete: () => void;
  qrcode?: string;
  device_id?: string;
  device_model?: string;
  device_os?: string;
}

function AppLogin2({ 
  container, 
  qrcode, 
  device_id,
  device_model,
  device_os,
  onLoginComplete,
 }: AppLoginProps) {
  const [pin, setPin] = useState("");
  const [stage2Response, setStage2Response] =
    useState<MobileLoginStep2Response>();
  const {
    phoneMoment,
    dg_id,
    profile,
    isLoggedIn,
    token,
    login,
    setPhoneMoment,
    setProfile,
    isPersonalization,
    setIsPersonalization
  } = useContext(AppContext);
  const notifCtx = useContext(AppNotifCtx);
  const router = useRouter();

  const [step1RequestBody, setStep1RequestBody] =
    useState<MobileLoginStep1Request | null>(null);
  const [step2RequestBody, setStep2RequestBody] =
    useState<MobileLoginStep2Request | null>(null);
  const [currentPage, setCurrentPage] = useState<Page>("mobile-number-page");
  const [currentFetch, setCurrentFetch] = useState<Fetch>("no-fetch");
  const [clientId, setClientId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [childrenInfo, setChildrenInfo] = useState<ChildInfo[]>();
  const [isReady, setIsReady] = useState(false);

  const [counter, setCounter] = useState(0);

  const encodePhoneMoment = (mobileStr: string, nickname: string) => {
    const thePhoneMoment = `${mobileStr}::${Date.now()}::${nickname}`;
    //console.log(thePhoneMoment);

    setPhoneMoment(thePhoneMoment);
  };

  const onStep1SuccessResponse = (data: MobileLoginStep1Response) => {
    encodePhoneMoment(step1RequestBody?.mobile || "", data.nickname);
    setCurrentFetch("no-fetch");
    setCurrentPage("verify-page");
    // setIsFirstLogin(data.is_first_login);
  };

  useEffect(() => {
    if (step1RequestBody) setCurrentFetch("fetch-1");
  }, [step1RequestBody]);

  useEffect(() => {
    if (!phoneMoment) return;

    if (phoneMoment) {
      const [mobile, moment, nickname] = phoneMoment.split("::");
      moment &&
        isNumeric(moment) &&
        Date.now() - parseInt(moment) < 60000 &&
        setCurrentPage("verify-page");
      return;
    }

    // NOT SURE about below lines!!!!
    setCurrentPage("mobile-number-page");
    setPhoneMoment("");
  }, []);

  useEffect(() => {
    if (!pin) return;
    if (pin.startsWith("modify-phone-number")) {
      // todo
      setPhoneMoment(""); // not sure?
      setCurrentPage("mobile-number-page");
      setCurrentFetch("no-fetch");
      return;
    }

    if (pin.startsWith("resend-pin-code")) {
      setStep1RequestBody(state => {
        return { ...state, resend: true };
      });
      return;
    }
    setStep2RequestBody({
      device_id: device_id ?? isMobile ? "mobile" : "desktop",
      mobile: phoneMoment.split("::")[0],
      nickname: phoneMoment.split("::")[2],
      verification_code: pin
    });
    setCurrentFetch("fetch-2");
  }, [pin]);

  useEffect(() => {
    const iaLoginPage = router.pathname === "/login" ? true : false;
    if (isPersonalization && iaLoginPage) {
      if (isReady && profile && isLoggedIn) {
        router.push(ROUTES.coldStart);
        setIsReady(false);
      }
      // if (profile && isLoggedIn) onLoginComplete();
    } else {
      if (profile && isLoggedIn) onLoginComplete();
    }
  }, [profile, isLoggedIn]);
  useEffect(() => {
    setCounter(1);
  }, []);

  return (
    <>
      {counter === 1 && (
        <FetchData
          url={API.request_rest_operator}
          method="GET"
          onSuccessful={data => {
            const newData = data as RestOperator;
            setIsPersonalization(
              newData?.json_content.is_personalisation_enabled
            );
          }}
          with_dg_id
          unAuthorized={true}
        />
      )}
      {currentPage === "mobile-number-page" && (
        <MobileNumberStep2
          container={container}
          setMobile={mobileStr => {
            setStep1RequestBody({
              device_id: device_id ?? isMobile ? "mobile" : "desktop",
              device_model: device_model ?? `browser-${mobileVendor}-${mobileModel}-${browserName}-${browserVersion}`,
              device_os: device_os ?? `NextJS-${osVersion}-${osName}`,
              mobile: mobileStr,
            });
          }}
          isLoading={isLoading}
        />
      )}
      {currentFetch === "fetch-1" && (
        <FetchData
          url={API.mobile_login_step1 + (qrcode ? "?qrcode=" + qrcode : "")}
          method="POST"
          body={JSON.stringify(step1RequestBody)}
          onSuccessful={data => {
            onStep1SuccessResponse(data as MobileLoginStep1Response);
          }}
          onNotSuccessful={() => {
            setPhoneMoment("");
            setCurrentFetch("no-fetch");
            setCurrentPage("mobile-number-page");
          }}
          pendingHandlerSetter={setIsLoading}
          unAuthorized
          showClientSideError
          showSuccess
        />
      )}
      {currentPage === "verify-page" && (
        <VerifyStep2
          container={container}
          setPinNumber={pin => setPin(pin)}
          isLoading={isLoading}
        />
      )}
      {currentPage === "select-child" && childrenInfo && (
        <SelectChildStep
          container={container}
          setClientId={id => {
            setClientId(id);
            setCurrentFetch("fetch-4");
          }}
          isLoading={isLoading}
          childrenInfo={childrenInfo}
        />
      )}
      {currentFetch === "fetch-2" && step2RequestBody && (
        <FetchData
          url={API.mobile_login_step2 + (qrcode ? "?qrcode=" + qrcode : "")}
          method="POST"
          body={JSON.stringify(step2RequestBody)}
          onSuccessful={data => {
            setCurrentPage("verify-page");
            setPhoneMoment("");
            setStage2Response(data as MobileLoginStep2Response);
            setCurrentFetch("fetch-3");
            trackEvent("Login", "Success", router?.asPath ?? "");
          }}
          on401={() => {
            notifCtx.showNotif({
              notifType: "error",
              message: "کد اشتباه است"
            });
            setCurrentFetch("no-fetch");
            setCurrentPage("verify-page");
          }}
          onNotSuccessful={() => {
            setStep2RequestBody(null);
            setCurrentFetch("no-fetch");
            setCurrentPage("verify-page");
            trackEvent("Login", "Unsuccess", router?.asPath ?? "");
          }}
          pendingHandlerSetter={setIsLoading}
          with_dg_id
          unAuthorized
          showClientSideError
        />
      )}

      {currentFetch === "fetch-3" && stage2Response && dg_id && (
        <FetchData
          url={API.request_children_list}
          method="GET"
          onSuccessful={data => {
            const castData = data as ChildInfo[];
            //console.log("castData: ", castData);
            if (castData.length > 1) {
              setCurrentPage("select-child");
              setChildrenInfo(castData);
            } else {
              const { token, refresh_token } = stage2Response;
              login(token, refresh_token, dg_id);
              setCurrentFetch("fetch-5");
            }
            // setProfile(castData);
          }}
          forcedToken={stage2Response.token}
          pendingHandlerSetter={setIsLoading}
          onNotSuccessful={response => {
            if (response.status) {
              // when there is no family
              //console.log("3-1 scenario");
              const { token, refresh_token } = stage2Response;
              login(token, refresh_token, dg_id);
              setCurrentFetch("fetch-5");
            }
          }}
        />
      )}
      {currentFetch === "fetch-4" && clientId && dg_id && (
        <FetchData
          url={API.get_token_user(clientId, isMobile ? "mobile" : "desktop")}
          method="GET"
          onSuccessful={data => {
            setCurrentFetch("fetch-5");
            const { token, refresh_token } = data as {
              token: string;
              refresh_token: string;
            };
            login(token, refresh_token, dg_id);
          }}
          with_dg_id
          showClientSideError
          forcedToken={stage2Response.token}
        />
      )}
      {currentFetch === "fetch-5" && token && (
        <FetchData
          url={API.request_profile_with_active_subs}
          method="GET"
          onSuccessful={data => {
            setCurrentFetch("no-fetch");
            const castData = data as MobileLoginStep3Response;
            setIsReady(true);
            setProfile(castData);
          }}
          with_dg_id
          showClientSideError
          pendingHandlerSetter={setIsLoading}
        />
      )}
    </>
  );
}

export default AppLogin2;
